import * as React from 'react';
import { Stack, Switch } from '@mantine/core';
import { useRoomContext, useTrackToggle } from '@livekit/components-react';
import { useKrispNoiseFilter } from '@livekit/components-react/krisp';
import { Track } from 'livekit-client';
import { useTranslation } from 'react-i18next';
import { DeviceSelect } from './device-select';
import { isLiveKitCloud, useNoiseSuppressionPreference } from './noise-suppression/noise-suppression';
import { isLowPowerDevice } from '@/shared/client-utils';

export function MicrophoneSettings() {
    const { t } = useTranslation('settings');
    const room = useRoomContext();
    const { enabled, pending, toggle } = useTrackToggle({ source: Track.Source.Microphone });

    return (
        <Stack gap="sm">
            <Switch
                checked={enabled}
                disabled={pending}
                label={t('microphone.toggle')}
                onChange={(event) => void toggle(event.currentTarget.checked)}
            />
            <DeviceSelect kind="audioinput" label={t('microphone.device')} />
            {isLiveKitCloud(room) ? <KrispNoiseFilterSwitch /> : <NoiseSuppressionSwitch />}
        </Stack>
    );
}

function NoiseSuppressionSwitch() {
    const { t } = useTranslation('settings');
    const [enabled, setEnabled] = useNoiseSuppressionPreference();

    return (
        <Switch
            checked={enabled}
            label={t('microphone.noiseFilter')}
            onChange={(event) => {
                setEnabled(event.currentTarget.checked);
            }}
        />
    );
}

function KrispNoiseFilterSwitch() {
    const { t } = useTranslation('settings');
    const { isNoiseFilterEnabled, setNoiseFilterEnabled, isNoiseFilterPending } = useKrispNoiseFilter({
        filterOptions: {
            bufferOverflowMs: 100,
            bufferDropMs: 200,
            quality: isLowPowerDevice() ? 'low' : 'medium',
            onBufferDrop: () => {
                console.warn('krisp buffer dropped, noise filter versions >= 0.3.2 will automatically disable the filter');
            },
        },
    });

    React.useEffect(() => {
        void setNoiseFilterEnabled(!isLowPowerDevice());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Switch
            checked={isNoiseFilterEnabled}
            disabled={isNoiseFilterPending}
            label={t('microphone.noiseFilter')}
            onChange={(event) => void setNoiseFilterEnabled(event.currentTarget.checked)}
        />
    );
}
